import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Users,
  UserPlus,
  Mail,
  Shield,
  Settings,
  MoreVertical,
  Edit,
  Trash2,
  CheckCircle,
  Clock,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { useWebSocket } from '../hooks/useWebSocket';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';

const Collaboration: React.FC = () => {
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');

  const { user } = useAuth();
  const toast = useToast();

  // 협업 세션 상태
  const [activeSession, setActiveSession] = useState<string | null>(null);
  const [documentContent, setDocumentContent] = useState('');
  const [comments, setComments] = useState<any[]>([]);
  const [onlineUsers, setOnlineUsers] = useState<any[]>([]);

  const { isConnected, participants, sendDocumentChange, sendComment, sendCursorUpdate } = useWebSocket({
    sessionId: activeSession || '',
    userId: String(user?.id || ''),
    token: localStorage.getItem('proposalcraft_access_token') || '',
    onMessage: (msg) => {
      switch (msg.type) {
        case 'document_change':
          setDocumentContent(msg.data?.content || documentContent);
          break;
        case 'comment_added':
          setComments(prev => [...prev, msg.data]);
          break;
        case 'participant_joined':
          toast.info(`${msg.data?.username || '사용자'}님이 참여했습니다`);
          break;
        case 'participant_left':
          toast.info(`${msg.data?.username || '사용자'}님이 나갔습니다`);
          break;
      }
    },
    onConnect: () => toast.success('협업 세션에 연결되었습니다'),
    onDisconnect: () => toast.warning('협업 세션 연결이 끊어졌습니다'),
    autoReconnect: true,
  });

  const [collaborators, setCollaborators] = useState<any[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sessions, setSessions] = useState<any[]>([]);

  const getApiBaseUrl = (): string => {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
      return `${protocol}//${hostname}:8001`;
    }
    return 'http://localhost:8001';
  };

  // 세션 및 멤버 로드
  useEffect(() => {
    const fetchSessions = async () => {
      setSessionsLoading(true);
      try {
        const token = localStorage.getItem('proposalcraft_access_token');
        const baseUrl = getApiBaseUrl();
        const userId = user?.id || '';

        if (!userId) {
          setSessionsLoading(false);
          return;
        }

        const response = await fetch(`${baseUrl}/api/collaboration/users/${userId}/active-sessions`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          const sessionList = Array.isArray(data) ? data : (data.sessions || []);
          setSessions(sessionList);

          // 세션에서 참여자 목록 추출
          const allMembers: any[] = [];
          const seenIds = new Set<string>();
          for (const session of sessionList) {
            const members = session.members || session.participants || [];
            for (const m of members) {
              const memberId = String(m.user_id || m.id);
              if (!seenIds.has(memberId)) {
                seenIds.add(memberId);
                const name = m.username || m.name || m.email || '사용자';
                allMembers.push({
                  id: memberId,
                  name,
                  email: m.email || '',
                  role: m.role || 'member',
                  status: m.is_online ? 'active' : 'active',
                  projects: [session.document_title || session.title || ''],
                  lastActive: m.last_active || '',
                  avatar: name.slice(0, 2).toUpperCase(),
                });
              }
            }
          }
          setCollaborators(allMembers);

          // 첫 세션을 활성 세션으로 설정
          if (sessionList.length > 0 && !activeSession) {
            setActiveSession(sessionList[0].id || sessionList[0].session_id);
          }
        }
      } catch (err) {
        console.error('Failed to fetch collaboration sessions:', err);
      } finally {
        setSessionsLoading(false);
      }
    };

    fetchSessions();
  }, [user?.id]);

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'editor':
        return 'bg-blue-100 text-blue-800';
      case 'viewer':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'inactive':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':
        return '관리자';
      case 'editor':
        return '편집자';
      case 'viewer':
        return '뷰어';
      default:
        return '멤버';
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    try {
      const token = localStorage.getItem('proposalcraft_access_token');
      const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? 'http://localhost:8001' : `${window.location.protocol}//${window.location.hostname}:8001`;

      const response = await fetch(`${baseUrl}/api/collaboration/sessions/${activeSession}/invite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: inviteEmail, role: inviteRole }),
      });

      if (response.ok) {
        toast.success(`${inviteEmail}님을 초대했습니다`);
        setShowInviteModal(false);
        setInviteEmail('');
      } else {
        toast.error('초대에 실패했습니다');
      }
    } catch (err) {
      toast.error('초대 중 오류가 발생했습니다');
    }
  };

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">협업</h1>
          <p className="text-gray-600 mt-2">팀원을 초대하고 프로젝트 협업을 관리하세요</p>
        </div>
        <div className="flex items-center space-x-3">
          <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm ${
            isConnected ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
          }`}>
            <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-gray-400'}`} />
            <span>{isConnected ? '연결됨' : '오프라인'}</span>
            {participants.length > 0 && <span>· {participants.length}명 참여</span>}
          </div>
          <button
            onClick={() => setShowInviteModal(true)}
            className="btn-primary"
          >
            <UserPlus className="h-5 w-5 mr-2" />
            팀원 초대
          </button>
        </div>
      </div>

      {/* 로딩 */}
      {sessionsLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 text-emerald-600 animate-spin" />
          <span className="ml-2 text-gray-600">협업 세션 로딩 중...</span>
        </div>
      )}

      {/* 빈 상태 */}
      {!sessionsLoading && collaborators.length === 0 && participants.length === 0 && (
        <div className="text-center py-12">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">아직 협업 세션이 없습니다</h3>
          <p className="text-gray-600">팀원을 초대하여 협업을 시작하세요</p>
        </div>
      )}

      {/* 통계 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">총 팀원</p>
              <p className="text-2xl font-bold text-gray-900">
                {collaborators.length + (participants.length > 0 ? participants.length : 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">활성 멤버</p>
              <p className="text-2xl font-bold text-gray-900">
                {collaborators.filter(c => c.status === 'active').length}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">초대 대기</p>
              <p className="text-2xl font-bold text-gray-900">
                {collaborators.filter(c => c.status === 'pending').length}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Shield className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">관리자</p>
              <p className="text-2xl font-bold text-gray-900">
                {collaborators.filter(c => c.role === 'admin').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 협업자 목록 */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">팀원 목록</h2>
          <div className="flex items-center space-x-2">
            <select className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent">
              <option value="all">모든 역할</option>
              <option value="admin">관리자</option>
              <option value="editor">편집자</option>
              <option value="viewer">뷰어</option>
            </select>
          </div>
        </div>
        
        <div className="space-y-4">
          {collaborators.map((collaborator) => (
            <div key={collaborator.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-semibold text-primary-600">{collaborator.avatar}</span>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">{collaborator.name}</h3>
                  <p className="text-sm text-gray-500">{collaborator.email}</p>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className={`px-2 py-1 text-xs rounded-full ${getRoleColor(collaborator.role)}`}>
                      {getRoleLabel(collaborator.role)}
                    </span>
                    <div className="flex items-center space-x-1">
                      {getStatusIcon(collaborator.status)}
                      <span className="text-xs text-gray-500">{collaborator.lastActive}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="text-sm text-gray-600">
                  <p className="font-medium">참여 프로젝트</p>
                  <p>{collaborator.projects.length}개</p>
                </div>

                <div className="flex items-center space-x-2">
                  <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                    <Edit className="h-4 w-4" />
                  </button>
                  <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                    <Settings className="h-4 w-4" />
                  </button>
                  <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                    <MoreVertical className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}

          {/* 실시간 참여자 목록 (WebSocket) */}
          {participants.map((participant, index) => {
            const initials = (participant.username || participant.name || '?')
              .slice(0, 2)
              .toUpperCase();
            return (
              <div key={`ws-participant-${participant.user_id || index}`} className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-100">
                <div className="flex items-center space-x-4">
                  <div className="relative w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-sm font-semibold text-green-700">{initials}</span>
                    <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">{participant.username || participant.name || '알 수 없음'}</h3>
                    <p className="text-sm text-gray-500">{participant.email || ''}</p>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                        온라인
                      </span>
                      <div className="flex items-center space-x-1">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span className="text-xs text-gray-500">지금 활성</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 초대 모달 */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-96 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">팀원 초대</h3>
              <button
                onClick={() => setShowInviteModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <span className="sr-only">닫기</span>
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  이메일 주소
                </label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="collaborator@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  역할
                </label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="viewer">뷰어 - 읽기만 가능</option>
                  <option value="editor">편집자 - 읽기 및 편집 가능</option>
                  <option value="admin">관리자 - 모든 권한</option>
                </select>
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => setShowInviteModal(false)}
                  className="flex-1 btn-secondary"
                >
                  취소
                </button>
                <button
                  onClick={handleInvite}
                  disabled={!inviteEmail.trim()}
                  className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  초대 보내기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Collaboration; 